/**
 * @file src/room/RoomUserCard.tsx
 * @description Somebody in a room, shown the way the platform shows people everywhere.
 *
 * The card is the library's `UserCard` — the very same component the website draws on a profile
 * hover — so a person looks identical here and there. This file is only what the library must not
 * know: that the data comes from the room's shared user-card cache, that a profile lives on the
 * website rather than in this app, and how following works from here.
 *
 * `RoomUserName` is the seat-row spelling of it: the name, with the card one hover away on a
 * pointer device and one tap away on a phone. Guests get neither, because they have no card.
 */
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { UserCard, UserCardPreview } from 'plantasia.space-design/react';

import { Constants } from '../config/Constants.js';
import { getAuthRoomIdentity } from './authIdentity.js';
import { getT } from '../i18n/index.js';
import { toggleUserFollow } from './followUser.js';
import { getUserCard, hasUserCard, loadUserCard, subscribeUserCards } from './userCards.js';
import { roomStore } from './roomStore.js';
import { getRoomDisplayName } from './identity.js';

/**
 * Portalled content resolves its tokens against the host document unless the app's own surface
 * class comes with it — without this the card paints black on black. Same bridge the waiting-room
 * dialog next door uses.
 */
const PORTAL_SURFACE = 'dark orb-studio__portal-surface';

/** Re-render whenever any card in the shared cache changes (a load landing, a follow flipping). */
function useCardSnapshot(userId: string | null) {
  const subscribe = useCallback((onChange: () => void) => subscribeUserCards(onChange), []);
  const read = useCallback(() => (userId ? getUserCard(userId) : null), [userId]);
  return useSyncExternalStore(subscribe, read, read);
}

/**
 * The shared card for one person, optionally fetching it. A room holds at most four people, so
 * the seat list loads all of them up front — that is four requests for the whole session, it
 * gives every row a real face, and it means opening someone's card costs nothing.
 */
export function useRoomUserCard(userId: string | null, { load = false } = {}) {
  const card = useCardSnapshot(userId);
  useEffect(() => {
    if (!load || !userId) return;
    void loadUserCard(userId);
  }, [load, userId]);
  return card;
}

/**
 * Who a room CLIENT is: the platform account behind it (for their card) and the name they are
 * showing. `peers` is the relay's view of the OTHERS, so this client is answered separately.
 * Null for a client the room cannot name — one that has already left, say.
 */
function readMemberIdentity(clientId: string | null) {
  if (!clientId) return null;
  const state = roomStore.state as { selfClientId?: string | null; peers?: Array<{ clientId: string; userId?: string | null; displayName?: string }> };
  if (clientId === state.selfClientId) {
    return { userId: getAuthRoomIdentity()?.userId ?? null, displayName: getRoomDisplayName() ?? null };
  }
  const peer = (state.peers ?? []).find((p) => p.clientId === clientId);
  return peer ? { userId: peer.userId ?? null, displayName: peer.displayName ?? null } : null;
}

/** Track a room member's identity, re-reading whenever the roster changes. */
export function useRoomMemberIdentity(clientId: string | null) {
  const [identity, setIdentity] = useState(() => readMemberIdentity(clientId));
  useEffect(() => {
    // Keep the PREVIOUS object when nothing about this member moved. The store notifies on every
    // peer join, grant change, phase change and score refresh, and a fresh object each time would
    // re-render every avatar on every tile for news about somebody else.
    const read = () => setIdentity((prev) => {
      const next = readMemberIdentity(clientId);
      if (prev === next) return prev;
      if (prev && next && prev.userId === next.userId && prev.displayName === next.displayName) {
        return prev;
      }
      return next;
    });
    read();
    return roomStore.subscribe(read);
  }, [clientId]);
  return identity;
}

/** The square avatar from a card, best size first. Null while unknown, and for guests. */
export function userCardAvatar(card: { images?: { square?: { small?: string | null; mid?: string | null; original?: string | null } | null } | null } | null | undefined) {
  const square = card?.images?.square;
  return square?.small || square?.mid || square?.original || null;
}

export function RoomUserCard({ userId, enabled = true }: { userId: string | null; enabled?: boolean }) {
  const card = useRoomUserCard(userId, { load: enabled });
  const [pending, setPending] = useState(false);
  const t = getT();

  const loading = Boolean(userId) && !hasUserCard(userId);

  const onFollow = useCallback(async () => {
    if (!userId || pending) return;
    setPending(true);
    try {
      await toggleUserFollow(userId);
    } finally {
      setPending(false);
    }
  }, [userId, pending]);

  const username = card?.username ?? null;
  const rootBase = String(Constants?.ROOT_BASE || '').replace(/\/+$/, '');
  const href = username && rootBase ? `${rootBase}/en/${encodeURIComponent(username)}` : undefined;

  // Never offered on your own card: the endpoint answers `viewerIsFollowing: false` for yourself
  // (you cannot follow you), so the button would look live and the server would refuse it.
  const isSelf = Boolean(userId) && userId === (getAuthRoomIdentity()?.userId ?? null);

  // Following is offered only once the card has said whether you already do — a button that
  // guessed would flip under the reader the moment the real answer landed.
  const action =
    !isSelf && card && typeof card.viewerIsFollowing === 'boolean' ? (
      <button
        type="button"
        onClick={onFollow}
        disabled={pending}
        aria-pressed={card.viewerIsFollowing}
        className="ps-room-follow"
      >
        {card.viewerIsFollowing ? t('room.card.following') : t('room.card.follow')}
      </button>
    ) : null;

  return (
    <UserCard
      user={card ?? undefined}
      loading={loading}
      href={href}
      // A profile is on the website, which is a different origin from this app — a real navigation,
      // so the plain anchor the library defaults to is the correct element.
      linkProps={href ? { target: '_blank', rel: 'noreferrer' } : undefined}
      action={action}
      labels={{ followers: t('room.card.followers'), following: t('room.card.followingCount') }}
    />
  );
}

/** A seat's name, with the card behind a hover or a tap. Guests render as plain text. */
export function RoomUserName({
  userId,
  name,
  children,
}: {
  userId: string | null;
  name: string;
  children: React.ReactNode;
}) {
  return (
    <UserCardPreview
      label={name}
      disabled={!userId}
      contentClassName={PORTAL_SURFACE}
      card={(open) => <RoomUserCard userId={userId} enabled={open} />}
    >
      {children}
    </UserCardPreview>
  );
}
